import { Button } from 'ui';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';
import CooperativeFormFields, {
  FormFields,
} from 'cooperatives/components/form/CooperativeFormFields';
import { Page } from 'dashboard/components/dashboard/Breadcrumbs';

import { useForm, SubmitHandler, ErrorOption } from 'react-hook-form';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { setFormError } from 'utils/form';
import {
  CooperativeCreated,
  CooperativeCreateMutationVariables,
  COOPERATIVE_CREATE_MUTATION,
} from 'cooperatives/graphql/cooperativeCreateMutation';
import {
  cooperativesTypename,
  CooperativesList,
  COOPERATIVES_QUERY,
} from 'cooperatives/graphql/cooperativesQuery';
import {
  CooperativesSelectList,
  COOPERATIVES_SELECT_QUERY,
  COOPERATIVES_SELECT_QUERY_VARIABLES,
} from 'cooperatives/graphql/cooperativesSelectQuery';
import useToastContext from 'ui/hooks/useToast';
import { useHistory } from 'react-router';
import { ToastProps } from 'ui/contexts/overlay/Toast';
import { useLoading } from 'ui/contexts/overlay/Loading';
import { useEffect } from 'react';
import { has } from 'lodash';

const DASHBOARD_COOPERATIVES_ROUTE = '/dashboard/cooperatives';

const BREADCRUMB_PAGES: Page[] = [
  {
    name: 'Gestão de cooperativas',
    route: DASHBOARD_COOPERATIVES_ROUTE,
    current: false,
  },
  { name: 'Cooperativas', route: DASHBOARD_COOPERATIVES_ROUTE, current: false },
  {
    name: 'Nova cooperativa',
    route: null,
    current: true,
  },
];
const TITLE = 'Nova cooperativa';
const CREATE_ERROR_TOAST: ToastProps = {
  text: 'Houve um erro ao tentar criar a cooperativa',
  title: 'Algo deu errado!',
  variant: 'danger',
};

const CREATE_SUCCESS_TOAST: ToastProps = {
  title: 'Successfully saved!',
  variant: 'primary',
  text: 'Anyone with a link can now view this file.',
};

export default function CreateCooperativesPage() {
  const { push } = useHistory();
  const { addToast } = useToastContext();
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormFields>();
  const { showLoading, closeLoading, LoadingOverlay } = useLoading();

  const [cooperativeCreateMutation, { loading: loadingCreate }] = useMutation<
    CooperativeCreated,
    CooperativeCreateMutationVariables
  >(COOPERATIVE_CREATE_MUTATION, {
    onError(error: ApolloError) {
      if (has(error.graphQLErrors[0], 'details')) {
        addToast(CREATE_ERROR_TOAST);
      }

      setFormError(
        error,
        (field: string, error: ErrorOption) => {
          setError(field as keyof FormFields, error);

          setTimeout(() => clearErrors(), 2500);
        }
        // t
      );
    },
    onCompleted() {
      addToast(CREATE_SUCCESS_TOAST);
      push(DASHBOARD_COOPERATIVES_ROUTE);
    },
  });

  const [cooperatives, { data: headquarters, loading }] =
    useLazyQuery<CooperativesSelectList>(
      COOPERATIVES_SELECT_QUERY,
      COOPERATIVES_SELECT_QUERY_VARIABLES
    );

  const onSubmit: SubmitHandler<FormFields> = (cooperativeCreateInput) =>
    cooperativeCreateMutation({
      variables: {
        cooperativeCreateInput,
      },
      update(cache, { data }) {
        const existingData = cache.readQuery<CooperativesList>({
          query: COOPERATIVES_QUERY,
        });
        cache.writeQuery({
          query: COOPERATIVES_QUERY,
          data: {
            cooperatives: {
              __typename: cooperativesTypename,
              afterCursor: existingData?.cooperatives.afterCursor || null,
              beforeCursor: existingData?.cooperatives.beforeCursor || null,
              entries: [
                data?.cooperativeCreate,
                ...(existingData?.cooperatives.entries || []),
              ],
            },
          },
        });
      },
    });

  const isLoading = loading || loadingCreate;

  useEffect(() => {
    if (isLoading) {
      showLoading();
      return;
    }

    closeLoading();
  }, [isLoading, showLoading, closeLoading]);

  useEffect(() => {
    cooperatives();
  }, [cooperatives]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingOverlay />
      <Dashboard
        dashboardHeader={<div className="h-10" />}
        dashboardMainHeaderTitle={
          <DashboardMainHeaderForm breadcrumbPages={BREADCRUMB_PAGES} title={TITLE}>
            <Button type="submit" size="sm" disabled={loading}>
              Salvar
            </Button>
          </DashboardMainHeaderForm>
        }
      >
        <CooperativeFormFields
          headquarters={headquarters?.cooperatives.entries}
          errors={errors}
          register={register}
        />
      </Dashboard>
    </form>
  );
}
